import React, { useState, useEffect, useRef, useCallback } from 'react'
import Script from 'next/script'
import clsx from 'clsx'
import styles from './style.module.css'
import { useTranslation } from '../../../../utils/convergence'
import { useIsApplePaylater, useApplePayState, useIsApplePaylaterWithApplePayRepayment, useAppRouter, useApplePay } from '../../../../state'
import { useIsDarkMode } from '@afterpaytouch/core'
import { useMonitoringWithEnduringEventProperties } from '../../../../integrations/monitoring'
import { PaymentCaptureType } from '../../../../model'

const APPLE_PAY_SDK_URL = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js'

export const ApplePayButton = () => {
  const { t } = useTranslation()
  const { isEnabledForDevice } = useApplePayState()
  const isApplePaylater = useIsApplePaylater()
  const isApplePayLaterApplePayRepaymentEnabled = useIsApplePaylaterWithApplePayRepayment()
  const isDarkMode = useIsDarkMode()
  const [isSDKLoaded, setIsSDKLoaded] = useState(false)
  const { locale } = useAppRouter()
  const applePayHelper = useApplePay()
  const sdkButtonRef = useRef<HTMLButtonElement>(null)
  const { logEvent } = useMonitoringWithEnduringEventProperties()

  useEffect(() => {
    if (typeof window !== 'undefined' && window.customElements?.get('apple-pay-button') !== undefined) {
      setIsSDKLoaded(true)
    }
  }, [])

  const onClick = useCallback(() => {
    try {
      applePayHelper.initialise(t, logEvent, PaymentCaptureType.RECURRING, isApplePayLaterApplePayRepaymentEnabled)
      applePayHelper.startSession()
    } catch (error) {
      return
    }
  }, [applePayHelper, t, logEvent, isApplePayLaterApplePayRepaymentEnabled])

  useEffect(() => {
    // Because the SDK button is in shadow DOM, we need to add the event listener this way
    if (sdkButtonRef.current) {
      sdkButtonRef.current.addEventListener('click', onClick)
    }

    return () => {
      if (sdkButtonRef.current) {
        sdkButtonRef.current.removeEventListener('click', onClick)
      }
    }
  }, [onClick])

  if (!isApplePaylater || !isApplePayLaterApplePayRepaymentEnabled || !isEnabledForDevice) {
    return null
  }

  const buttonClass = clsx(styles.applePayButton, isDarkMode && styles.darkMode)

  // The "Continue with Apple Pay" copy is only supported when rendered as <apple-pay-button> which
  // requires the SDK. But we can still show the button using the fallback button implementation which
  // relies on --webkit-appearance: -apple-pay-button;
  return (
    <>
      <Script
        src={APPLE_PAY_SDK_URL}
        strategy='afterInteractive'
        onLoad={() => {
          setIsSDKLoaded(true)
        }}
      />
      {isSDKLoaded ? (
        // @ts-expect-error - Apple Pay custom element not in JSX types
        <apple-pay-button ref={sdkButtonRef} class={styles.sdkApplePayButton} buttonstyle={isDarkMode ? 'white' : 'black'} type='continue' locale={locale} />
      ) : (
        <button onClick={onClick} type='button' className={buttonClass} aria-label={t('summary:applePay.continueWithApplePay')} />
      )}
    </>
  )
}
